import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import { useOrgDesignConfig } from "src/insights/useOrgDesignConfig";
import { toOverlayCatalogEntries } from "src/overlayAttributes/overlayAttributeCatalog";
import { useOverlayAttributeCatalogQuery } from "src/overlayAttributes/useOverlayAttributeCatalogQuery";
import { getWorkspaceTagTypesList } from "src/pages/Workspaces/workspace.graphql";

import {
  getAttributePolicy,
  isAttributeHiddenFromOrgChart,
} from "./attributePolicy";
import {
  gradeFieldOptions,
  type GradeCatalogRow,
  type GradeFieldOption,
  type GradeSource,
  type GradeTagTypeRow,
} from "./gradeCompression";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const tagTypesFromQueryData = (data: unknown): GradeTagTypeRow[] => {
  if (!isRecord(data) || !Array.isArray(data["tagTypes"])) {
    return [];
  }
  return data["tagTypes"].flatMap((row) => {
    if (!isRecord(row) || typeof row["id"] !== "string" || !row["id"]) {
      return [];
    }
    return [
      {
        id: row["id"],
        name: typeof row["name"] === "string" ? row["name"] : null,
        allowPersonToHaveTag: row["allowPersonToHaveTag"] === true,
        allowPositionToHaveTag: row["allowPositionToHaveTag"] === true,
        isActive: row["isActive"] !== false,
      },
    ];
  });
};

export const useGradeFieldOptions = (
  source: GradeSource
): { options: GradeFieldOption[]; loading: boolean } => {
  const wantsAttributes =
    source === "personAttribute" || source === "positionAttribute";
  const wantsTags = source === "personTag" || source === "positionTag";
  const catalogQuery = useOverlayAttributeCatalogQuery({
    skip: !wantsAttributes,
  });
  const tagQuery = useQuery(getWorkspaceTagTypesList, {
    variables: { includeInactive: false },
    skip: !wantsTags,
  });
  const { attributePolicies, attributeLabels } = useOrgDesignConfig();

  const catalog = useMemo((): GradeCatalogRow[] => {
    const entries = toOverlayCatalogEntries({
      personCatalog: catalogQuery.personCatalog,
      positionCatalog: catalogQuery.positionCatalog,
      teamCatalog: catalogQuery.teamCatalog,
    });
    return entries.map((entry) => ({
      key: entry.key,
      label: entry.label ?? null,
      entityTypes: entry.entityTypes,
      ...(entry.settingsExcluded ? { settingsExcluded: true } : {}),
      ...(entry.internal ? { internal: true } : {}),
      hidden: isAttributeHiddenFromOrgChart(
        getAttributePolicy(attributePolicies, entry.key, entry)
      ),
    }));
  }, [
    attributePolicies,
    catalogQuery.personCatalog,
    catalogQuery.positionCatalog,
    catalogQuery.teamCatalog,
  ]);
  const tagTypes = useMemo(
    () => tagTypesFromQueryData(tagQuery.data),
    [tagQuery.data]
  );
  const options = useMemo(
    () => gradeFieldOptions(source, catalog, tagTypes, attributeLabels),
    [source, catalog, tagTypes, attributeLabels]
  );

  return {
    options,
    loading: Boolean(
      (wantsAttributes && catalogQuery.loading) ||
      (wantsTags && tagQuery.loading)
    ),
  };
};
